// Plain-English words for the Quoting page (Lane 1335). Every flag the reader
// (rfq_extract), the matcher (rfq_match) and the worker can put on a line or a
// request has a sentence here; an unknown one falls back to its words, never
// to nothing (Ryan: flag rather than hide).

const LINE_FLAGS = {
  // The part Wingman filled in, and why to check it (rfq_match, Lane 1332).
  part_filled_in_from_sales_history: 'Wingman filled in this XMEK part from sales history. Check it.',
  fill_in_check_source_restriction: 'The customer limits the brand or source (for example "Parker only"). Check that this part meets it.',
  fill_in_check_part_number_from_scan: 'The part number was read from a scanned page. Check it against the drawing.',
  fill_in_check_material_not_listed: "This part's material is not one the customer lists.",
  fill_in_check_part_not_active: 'This part is no longer active, or has not sold in a year.',
  fill_in_check_several_parts: 'Several XMEK parts were sold under this customer part number. This is the most recent one.',
  fill_in_check_sold_once: 'Sold only once under this customer part number.',
  fill_in_check_size_only: 'Picked by size and a material the customer lists. Never sold under this customer part number. Confirm it or pick another.',
  // Wingman recommended (rfq_match, Lane 1357): needs the quoter's confirm.
  part_listed_by_customer: "The customer's file lists this XMEK part number.",
  part_recommended_confirm: 'Wingman recommends this part because its size and material fit the line. It was never sold under this customer part number. Confirm it or pick another.',
  fill_in_check_hardness_not_stated: 'The line states no hardness, so hardness was not compared. Check it.',
  fill_in_check_spec_not_checked: "The customer's material spec (for example ES4092) is not checked.",
  more_candidates_than_shown: 'More matching parts exist than are listed here.',
  matching_failed: 'Wingman could not look up this line. Pick a part with the search box.',
  sku_too_long_for_column: 'The matched part number is too long to store. Pick a part with the search box.',
  // Reading the email (rfq_extract, rfq_worker_service).
  part_number_unverified: 'The part number could not be found in the email. Check it.',
  read_from_scan: 'Read from a scanned page. Check it against the picture.',
  revision_from_another_part: 'The revision may belong to another part.',
  quantity_from_another_part: 'The quantity may belong to another part.',
  quantity_rejected: 'A quantity was found but could not be confirmed.',
  quantity_words_not_read: 'The quantity is written in words that could not be read.',
  quantity_may_be_stated: 'A quantity may be written somewhere Wingman could not read.',
  quantity_missing_from_answer: 'No quantity was found for this line.',
  quantity_separator_ambiguous: 'The quantity uses a separator that could mean thousands or decimals. Check it.',
  quantity_list_shared_by_all_lines: 'These quantities are stated once for every line.',
  quantity_list_scope_unproven: 'The quantity list may not apply to every line.',
  quantity_list_cut: 'The quantity list was too long; not all of it is shown.',
  quantity_wording_shortened: 'The quantity wording was too long and is shortened.',
  quantity_only_in_forwarding_table: 'The quantity comes only from the table XMEK added when forwarding, not from the customer.',
  quantity_differs_from_forwarding_table: 'The customer\'s quantity differs from the table XMEK added when forwarding. The customer\'s is shown.',
  quantity_matches_forwarding_table: 'The quantity matches the table XMEK added when forwarding.',
  quantity_in_table_like_xmek_forwarding: 'The quantity comes from a table like the one XMEK adds when forwarding.',
  uom_not_stated: 'The unit is not stated; Wingman reads the quantity as each.',
  uom_needs_review: 'Check the unit.',
  spec_size_not_confirmed: 'A size Wingman read for this part could not be confirmed in the email, so no part was searched by size. Check the size.',
  need_by_not_read: 'The need-by date could not be read.',
  need_by_rejected: 'A need-by date was found but could not be confirmed.',
  cpn_too_long_for_column: 'The customer part number is too long to store in full.',
  revision_too_long_for_column: 'The revision is too long to store in full.',
};

const HEADER_FLAGS = {
  customer_name_read_from_scan: 'The customer name was read from a scanned page.',
  customer_rfq_number_read_from_scan: 'The RFQ number was read from a scanned page.',
  customer_rfq_number_too_long_for_column: 'The RFQ number is too long to store in full.',
  rfq_number_is_a_part_number: 'The RFQ number looked like a part number and was not used.',
  reply_due_not_read: 'The reply-by date could not be read.',
  reply_due_read_from_scan: 'The reply-by date was read from a scanned page.',
  reply_due_rejected: 'A reply-by date was found but could not be confirmed.',
  not_rfq_but_quote_wording: 'This may not be a request for quote.',
  not_rfq_but_request_words: 'This may be a request for quote even though it looks like a notice.',
};

const GAP_REASONS = {
  line_limit: 'Only the first lines were read; this RFQ has more.',
  quantity_limit: 'Some quantities were not read; there were too many.',
  result_size_limit: 'The RFQ was too large to read in full; the last lines are missing.',
  no_lines_kept: 'No lines could be confirmed.',
  // Why a file or the whole email was not read in full (Ryan, 2026-09-27:
  // plain words, not codes).
  reader_text_limit: "not read in full: the email's reading limit was reached (large drawing packs often reach it). Check it if it may hold something to quote.",
  reader_line_cut: 'some lines were too long to read in full.',
  reader_no_answer: 'the reader gave no answer. Check the email.',
  reader_answer_cut_off: "the reader's answer was cut off; some lines may be missing.",
  reader_multiple_answers: 'the reader gave two answers, so neither was used. Check the email.',
  ai_budget_reached: "today's reading limit was reached before this was read.",
  ocr_pending: 'a scanned page was not read yet.',
  ocr_cut_off: 'a scanned page was only partly read.',
  ocr_limit: 'too many scanned pages; the rest were not read.',
  ocr_refused: 'a scanned page could not be read.',
  ocr_unreadable: 'a scanned page could not be opened.',
  needs_ocr: 'a scanned page was not read.',
  no_text_layer: 'a scanned page was not read.',
  page_limit: 'too many pages; the rest were not read.',
  time_limit: 'took too long to open.',
  image_too_large: 'the picture is too large to read.',
  unsupported_type: 'a file type Wingman cannot read.',
  unsupported_compression: 'a compressed file Wingman cannot open.',
};

const JOB_REASONS = {
  intake_disabled: 'Reading is paused for now.',
  tenant_cap: "Today's reading limit is reached. It continues tomorrow.",
};

function words(code) {
  return String(code || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) + '.';
}

export function lineFlagText(code) { return LINE_FLAGS[code] || words(code); }
export function headerFlagText(code) { return HEADER_FLAGS[code] || words(code); }
export function gapText(gap) {
  const what = gap.filename ? `${gap.filename}: ` : '';
  // A file cut by the reading limit says whether part of it was read (Lane 1357).
  const limit = gap.reason === 'reader_text_limit' && gap.partly !== undefined
    ? (gap.partly ? "only partly read: the email's reading limit was reached. Check it if it may hold something to quote."
      : "not read: the email's reading limit was reached first. Check it if it may hold something to quote.")
    : null;
  const text = limit || GAP_REASONS[gap.reason] || `not read (${String(gap.reason || 'unknown').replace(/_/g, ' ')}).`;
  return what ? what + text : text.charAt(0).toUpperCase() + text.slice(1);
}

// Flags that are about the fill-in (shown first, in the matcher's order).
export const FILL_FLAG_ORDER = [
  'fill_in_check_source_restriction', 'fill_in_check_part_number_from_scan',
  'fill_in_check_material_not_listed', 'fill_in_check_part_not_active',
  'fill_in_check_several_parts', 'fill_in_check_sold_once', 'fill_in_check_size_only',
  'fill_in_check_hardness_not_stated', 'fill_in_check_spec_not_checked', 'part_recommended_confirm', 'part_filled_in_from_sales_history', 'part_listed_by_customer',
];

// Why a line has no XMEK part at all (Lane 1357, F7). Facts only: never
// "XMEK does not sell this".
export function blankReason(line) {
  const fill = line.match_basis?.fill_in;
  if (fill === 'part_number_unverified') return 'The part number could not be found in the email, so it was not looked up.';
  if (fill === 'matching_failed') return 'Wingman could not look up this line.';
  const history = fill === 'no_part_number'
    ? 'No part number was read for this line'
    : 'No XMEK sales under this customer part number or another spelling of it';
  if (line.candidates.some((c) => c.source === 'loose_part_number')) {
    return 'Sold before under another spelling of this customer part number. Check the parts below and pick one.';
  }
  if (line.candidates.length) return `${history}. Wingman found the parts below; none is a clear fit, so pick one or search.`;
  // Lane 1391: the AI's search over every XMEK part.
  if ((line.suggested || []).length) return `${history}. Wingman searched XMEK's parts and suggests the ones below: check them and pick one.`;
  if (line.suggested_by === 'none_fit') return `${history}, and Wingman found no XMEK part like it. Quote it from a vendor.`;
  return line.size_read
    ? `${history}, and no XMEK part has the size this line names. Search below, or quote it from a vendor.`
    : `${history}, and no size Wingman can read. Search below, or quote it from a vendor.`;
}
// Flags that only say something is fine; never shown as a warning.
// The forwarding-table notes are about how XMEK forwards, not about the part
// (Ryan, 2026-09-27: "get rid of that"). A customer quantity that DIFFERS from
// the table still shows.
export const QUIET_FLAGS = new Set([
  'quantity_matches_forwarding_table', 'quantity_list_shared_by_all_lines',
  'quantity_in_table_like_xmek_forwarding', 'quantity_only_in_forwarding_table',
  // Shown in the line's "From the customer's file" box, not a warning (Lane 1399).
  'part_listed_by_customer',
]);

export function jobText(job, intakeEnabled) {
  if (!job) return '';
  switch (job.state) {
    case 'queued': return intakeEnabled ? 'Waiting to be read' : 'Waiting: reading is paused';
    case 'waiting': return 'Waiting: ' + (JOB_REASONS[job.reason] || words(job.reason)).replace(/\.$/, '');
    case 'reading': return 'Being read';
    case 'updating': return 'Updating';
    case 'failed': return 'Could not be read (' + String(job.reason || 'other').replace(/_/g, ' ') + ')';
    case 'cancelled': return 'Stopped';
    default: return '';
  }
}

// The customer as the page names it: the confirmed account's name (Lane
// 1357), else the name the email wrote, else the sender's email domain
// (Lane 1348), marked as such; never a guessed name.
export function customerLabel(r) {
  if (r.customer_state === 'resolved' && r.customer_name) return r.customer_name;
  if (r.customer_name_as_written) return r.customer_name_as_written;
  if (r.customer_hint?.domain) return `${r.customer_hint.domain} (sender's address)`;
  return null;
}

// Quantities as the customer asked for them (Lane 1349). One quantity reads
// as itself; several (price breaks, or "100 or 500") read "Tiered" in the
// table, and the tiers are listed where the quoter prices them.
export function scenarioText(s) {
  if (!s) return '?';
  if (s.kind === 'single') return qty(s.qty);
  if (s.kind === 'break_range') {
    const { min, max } = s.qty || {};
    if (min != null && max != null) return `${qty(min)}–${qty(max)}`;
    if (min != null) return `${qty(min)}+`;
    if (max != null) return `up to ${qty(max)}`;
  }
  if (s.kind === 'not_given') return 'not given';
  return s.as_written || '?';
}
const unitOf = (list) => {
  const units = [...new Set(list.map((s) => String(s.uom || '').trim().toLowerCase()).filter(Boolean))];
  return units.length === 1 ? ` ${list.find((s) => s.uom).uom}` : '';
};
// A scenario written as numbers (a unit can be added); anything else is shown
// as the customer wrote it, which may already carry its unit (Codex 1349).
const structured = (s) => s && (s.kind === 'single' || s.kind === 'break_range');
export function tiersText(scenarios) {
  const list = scenarios || [];
  if (!list.length) return '';
  const units = new Set(list.map((s) => String(s.uom || '').trim().toLowerCase()));
  // All numeric and one unit: say it once at the end; else on each tier.
  if (units.size <= 1 && list.every(structured)) return list.map(scenarioText).join(', ') + unitOf(list);
  return list.map((s) => `${scenarioText(s)}${structured(s) && s.uom ? ` ${s.uom}` : ''}`).join(', ');
}
export function qtyCellText(scenarios) {
  const list = scenarios || [];
  if (!list.length) return '–';
  if (list.length > 1) return 'Tiered';
  const s = list[0];
  if (s.kind === 'single') return `${qty(s.qty)}${s.uom ? ` ${s.uom}` : ''}`;
  if (s.kind === 'not_given') return 'Not given';
  return `${scenarioText(s)}${s.kind === 'break_range' && s.uom ? ` ${s.uom}` : ''}`;
}
// The same tiers on every line: the RFQ asks for these breaks (shown once in
// the summary). Different tiers per line: null (each line shows its own).
export function sharedTiers(lines) {
  // Kind, numbers, unit, wording and origin all equal (Codex 1349): two
  // different unreadable requests are never shown as one.
  const key = (l) => JSON.stringify((l.quantity_scenarios || []).map((s) => [
    s.kind, s.qty, String(s.uom || '').toLowerCase(), s.as_written || null, s.origin || null]));
  const tiered = (lines || []).filter((l) => (l.quantity_scenarios || []).length > 1);
  if (!tiered.length || tiered.length !== (lines || []).length) return null;
  return tiered.every((l) => key(l) === key(tiered[0])) ? tiersText(tiered[0].quantity_scenarios) : null;
}

// Numbers and dates, as the mockup shows them.
export function money(v, currency) {
  if (v === null || v === undefined || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  // Below a cent, enough places that a real price never shows as $0.000.
  const digits = n >= 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : Math.abs(n) >= 0.01 || n === 0 ? { minimumFractionDigits: 3, maximumFractionDigits: 3 }
      : { minimumFractionDigits: 3, maximumSignificantDigits: 3 };
  // Another currency is named, never shown with a dollar sign (Codex 1335).
  if (currency && currency !== 'USD') return `${n.toLocaleString('en-US', digits)} ${currency}`;
  return '$' + n.toLocaleString('en-US', digits);
}

// Whole dollars, for totals such as an account's sales (no cents).
export function moneyWhole(v) {
  if (v === null || v === undefined || v === '') return '–';
  const n = Number(v);
  return Number.isFinite(n) ? '$' + Math.round(n).toLocaleString('en-US') : '–';
}

// "per BOX" when a price is not per piece; nothing when it is.
export function perUnit(uom) {
  const u = String(uom || '').trim();
  return !u || ['each', 'ea', 'pc', 'pcs', 'piece'].includes(u.toLowerCase()) ? '' : ` per ${u}`;
}
export function pct(v) {
  if (v === null || v === undefined) return '–';
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) + '%' : '–';
}
export function qty(v) {
  if (v === null || v === undefined || v === '') return '–';
  const n = Number(v);
  // Whole numbers as whole; a fraction (2.5 feet) is kept, never rounded away.
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 3 }) : '–';
}
export function shortDate(s) {
  if (!s) return '–';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return y && m && d ? `${+m}/${+d}/${y.slice(2)}` : '–';
}
